import { useState } from 'react'
import clsx from 'clsx'
import { sendSmsRequest } from '../../../api'
import { SHOW_ERROR, SHOW_SUCCESS } from '../../../util'

type CountryCode = '+44' | '+1' | '+92'

const MAX_DIGITS: Record<CountryCode, number> = {
    '+44': 10, // UK national significant number length (without country code)
    '+1': 10,  // US NANP length
    '+92': 10, // Pakistan mobile number (without country code)
}

function sanitizeDigits(input: string): string {
    return input.replace(/\D/g, '')
}

function isValidNumber(countryCode: CountryCode, digits: string): boolean {
    if (!digits) return false
    if (countryCode === '+44') {
        // UK mobile numbers often begin with 7 when written nationally (07...)
        // We accept any 10 digits for admin flexibility but cap length.
        return digits.length === MAX_DIGITS['+44']
    }
    return digits.length === MAX_DIGITS[countryCode]
}

export default function Messages() {
    const [countryCode, setCountryCode] = useState<CountryCode>('+1')
    const [inputDigits, setInputDigits] = useState('')
    const [recipients, setRecipients] = useState<string[]>([])
    const [message, setMessage] = useState('')
    const [error, setError] = useState<string>('')
    const [sending, setSending] = useState<boolean>(false)

    const handleAdd = () => {
        setError('')
        const digits = sanitizeDigits(inputDigits).slice(0, MAX_DIGITS[countryCode])
        if (digits.length === 0) {
            // Ignore empty submission without showing an error
            return
        }
        if (!isValidNumber(countryCode, digits)) {
            setError(
                countryCode === '+44'
                    ? 'Enter a valid UK number (10 digits after +44)'
                    : 'Enter a valid US number (10 digits after +1)'
            )
            return
        }
        const formatted = `${countryCode}${digits}`
        if (recipients.includes(formatted)) {
            setError('Number already added')
            return
        }
        setRecipients((prev) => [formatted, ...prev])
        setInputDigits('')
    }

    const handleRemove = (value: string) => {
        setRecipients((prev) => prev.filter((n) => n !== value))
    }

    const handleClear = () => {
        setRecipients([])
    }

    const remaining = 500 - message.length

    const canSend = recipients.length > 0 && message.trim().length > 0

    const onSend = async () => {
        try {
            setSending(true)
            const numbersCsv = recipients.join(',')
            const res: any = await sendSmsRequest(numbersCsv, message.trim())
            if (res?.status === 200) {
                SHOW_SUCCESS(true, 'SMS sent successfully')
                // Clear inputs after success
                setMessage('')
                setRecipients([])
                setInputDigits('')
            } else if (res?.response?.data?.message) {
                SHOW_ERROR(true, res.response.data.message)
            } else if (res?.data?.message) {
                SHOW_ERROR(true, res.data.message)
            } else {
                SHOW_ERROR(true, 'Failed to send SMS')
            }
        } catch (e: any) {
            SHOW_ERROR(true, 'Failed to send SMS')
        } finally {
            setSending(false)
        }
    }

    return (
        <>
            <div className='col-6'>
                <h2 className='py-3 mb-2'>Send a Text Message</h2>
            </div>
            <div className='card'>
                <div className='card-body pt-0'>
                    <div className='row g-6 align-items-start mt-4'>
                        <div className='col-md-2'>
                            <label className='form-label'>Country code</label>
                            <select
                                className='form-select form-select-solid'
                                value={countryCode}
                                onChange={(e) => setCountryCode(e.target.value as CountryCode)}
                            >
                                <option value='+44'>UK (+44)</option>
                                <option value='+1'>US (+1)</option>
                                {/* <option value='+92'>Pakistan (+92)</option> */}
                            </select>
                        </div>
                        <div className='col-md-6'>
                            <label className='form-label'>Phone number</label>
                            <input
                                className={clsx('form-control form-control-solid')}
                                placeholder={
                                    countryCode === '+44'
                                        ? 'UK number (10 digits)'
                                        : countryCode === '+1'
                                            ? 'US number (10 digits)'
                                            : 'PK number (10 digits)'
                                }
                                value={inputDigits}
                                onChange={(e) => {
                                    const digits = sanitizeDigits(e.target.value)
                                    const sliced = digits.slice(0, MAX_DIGITS[countryCode])
                                    setInputDigits(sliced)
                                    if (sliced.length === 0) {
                                        setError('')
                                    } else if (error && isValidNumber(countryCode, sliced)) {
                                        setError('')
                                    }
                                }}
                                inputMode='numeric'
                                pattern='[0-9]*'
                            />
                            {error ?
                                <div className='text-danger mt-2'>{error}</div>
                                :
                                <div className='form-text'>Max {MAX_DIGITS[countryCode]} digits after {countryCode}</div>
                            }
                        </div>
                        <div className='col-md-2'>
                            <button type='button' className='btn btn-primary w-100 mt-8' onClick={handleAdd}>
                                Add
                            </button>
                        </div>
                        <div className='col-md-2'>
                            <button type='button' className='btn btn-light w-100 mt-8' onClick={handleClear} disabled={recipients.length === 0}>
                                Clear list
                            </button>
                        </div>
                    </div>

                    <div className='separator my-8'></div>

                    <div className='mb-7'>
                        <label className='form-label'>Recipients</label>
                        {recipients.length === 0 ? (
                            <div className='text-muted'>No recipients yet. Add numbers above.</div>
                        ) : (
                            <div className='d-flex flex-wrap gap-2'>
                                {recipients.map((num) => (
                                    <span key={num} className='badge badge-light d-inline-flex align-items-center'>
                                        <span className='me-2'>{num}</span>
                                        <button
                                            type='button'
                                            className='btn btn-sm btn-icon btn-light-danger'
                                            onClick={() => handleRemove(num)}
                                            aria-label={`Remove ${num}`}
                                        >
                                            <i className='bi bi-x-lg'></i>
                                        </button>
                                    </span>
                                ))}
                            </div>
                        )}
                    </div>

                    <div className='mb-10'>
                        <label className='form-label'>Message</label>
                        <textarea
                            className='form-control form-control-solid'
                            rows={5}
                            maxLength={500}
                            value={message}
                            onChange={(e) => setMessage(e.target.value)}
                            placeholder='Type your message here...'
                        />
                        <div className='form-text'>{remaining} characters remaining</div>
                    </div>

                    <div className='d-flex justify-content-end'>
                        <button type='button' className='btn btn-primary' disabled={!canSend || sending} onClick={onSend}>
                            {sending ? 'Sending...' : 'Send message'}
                        </button>
                    </div>
                </div>
            </div>
        </>
    )
}


